import { RootState } from "@/app/store";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { auth as authClient } from "@/core/api";
import { showNotification } from "@/components/basic/NotificationBar/notificationBarSlice";

export interface State {
  isRegister: boolean;
  isAuthenticate: boolean;
  status: "idle" | "loading" | "failed";
  userInfo?: {
    [key: string]: any;
  };
}

const initialState: State = {
  isRegister: false,
  isAuthenticate: false,
  status: "idle",
  userInfo: undefined,
};

export const checkEmailIsRegistered = createAsyncThunk(
  "auth/checkEmailIsRegistered",
  async (email: string) => {
    const response = await authClient.checkEmailIsRegistered(email);
    return response;
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async (
    { email, password }: { email: string; password: string },
    { dispatch }
  ) => {
    const response = await authClient.login({ email, password });
    let message = "";
    if (typeof response === "object") {
      message = "Login successful.";
    } else {
      message = "Login failed, please try again.";
    }

    if (message) {
      dispatch(showNotification(message));
    }
    return response;
  }
);

export const getUserInfo = createAsyncThunk("auth/getUserInfo", async () => {
  const response = await authClient.getUserInfo();
  return response;
});

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async ({ email }: { email: string }, { dispatch }) => {
    const response = await authClient.forgotPassword({ email });
    let message = "";

    if (response === "NA") {
      message = "System does not exist this email, please check the email.";
    } else if (response === "OK") {
      message = "System has sent an reset email, please check it.";
    } else if (response === "FAIL") {
      message = "Reset failed, please try again.";
    }

    if (message) {
      dispatch(showNotification(message));
    }
    return response;
  }
);

export const addressSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(checkEmailIsRegistered.pending, (state) => {
        state.status = "loading";
      })
      .addCase(checkEmailIsRegistered.fulfilled, (state, action) => {
        state.status = "idle";
        const payload = action.payload as unknown as "USEREXISTS" | "OK";

        state.isRegister = payload === "USEREXISTS";
      });

    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "idle";
        const payload = action.payload;
        if (typeof payload === "object") {
          const transformData = {};
          Object.keys(payload).forEach((key) => {
            if (key !== "password") {
              transformData[key.replace("session_", "")] = payload[key];
            }
          });
          state.userInfo = transformData;
          state.isAuthenticate = true;
        } else {
          state.isAuthenticate = false;
        }
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.status = "idle";
        const payload = action.payload;
        if (typeof payload === "object") {
          const transformData = {};
          Object.keys(payload).forEach((key) => {
            if (key !== "password") {
              transformData[key.replace("session_", "")] = payload[key];
            }
          });
          state.userInfo = transformData;
          state.isAuthenticate = true;
        } else {
          state.isAuthenticate = false;
        }
      });
  },
});

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectEmailIsRegister = (state: RootState) =>
  state.auth.isRegister;

export const selectUserInfo = (state: RootState) => state.auth.userInfo;

export const selectIsAuthenticate = (state: RootState) =>
  state.auth.isAuthenticate;

export default addressSlice.reducer;
